/**
 * AP-773 — step factories for viewer actions (§3.1 A1–A3).
 *
 * The worksheet "after" snapshot is captured lazily on the first undo: the
 * worksheet folds a reshape in a React effect after mouse-up, so it is not
 * available at commit time — but by the time the reader undoes, the top step
 * is by construction the last thing that changed the worksheet (I3, linear).
 */
import type { Dispatch } from '@reduxjs/toolkit';
import { selectAnnotation } from '@/state/slices/measurementSession';
import { annotation as csAnnotation } from '@cornerstonejs/tools';
import {
  discardHistoryStep,
  runApplyingHistory,
  type HistoryLocation,
  type HistoryStep,
} from './historyStore';
import {
  restoreAnnotationData,
  type AnnotationDataSnapshot,
} from './annotationSnapshot';
import {
  applyWorksheetSnapshot,
  getWorksheetSnapshot,
  type WorksheetSnapshot,
} from './worksheetApplier';
import { showStepLocation } from './viewerNavigation';
import {
  restoreSessionResults,
  type SessionResultsSnapshot,
} from './sessionResults';

interface ReshapeStepParams {
  annotationUID: string;
  before: AnnotationDataSnapshot;
  after: AnnotationDataSnapshot;
  worksheetBefore: WorksheetSnapshot | null;
  /** Simpson / Doppler sibling results on either side of the drag (§3.2). */
  resultsBefore: SessionResultsSnapshot;
  resultsAfter: SessionResultsSnapshot;
  dispatch: Dispatch;
  location?: HistoryLocation;
  /** R20 message label, e.g. "LVEDV reshaped". */
  label: string;
}

/** A2 — annotation reshaped / moved (one drag = one step). */
export function createReshapeStep({
  annotationUID,
  before,
  after,
  worksheetBefore,
  resultsBefore,
  resultsAfter,
  dispatch,
  location,
  label,
}: ReshapeStepParams): HistoryStep {
  let worksheetAfter: WorksheetSnapshot | null = null;

  /** Leaving a DICOM unloads its hydrated annotations; returning re-creates
   *  them. So "exists" is only meaningful once the location is on screen. */
  const targetExists = () => !!csAnnotation.state.getAnnotation(annotationUID);

  // The step object, so a deferred apply can discard itself (R14).
  let self: HistoryStep | null = null;

  /** Apply one side of the step atomically (I2) once its location is shown;
   *  R17: the affected annotation becomes the selected one. R14: if the
   *  annotation is still missing after navigation, change nothing and drop
   *  the step. */
  const apply = (
    data: AnnotationDataSnapshot,
    results: SessionResultsSnapshot,
    worksheet: WorksheetSnapshot | null
  ) => {
    if (!targetExists()) {
      if (self) discardHistoryStep(self);
      return;
    }
    runApplyingHistory(() => {
      dispatch(selectAnnotation(annotationUID));
      restoreAnnotationData(annotationUID, data);
      restoreSessionResults(dispatch, annotationUID, results);
      if (worksheet) applyWorksheetSnapshot(worksheet);
    });
  };

  self = {
    action: 'A2',
    label,
    location,
    undo: () => {
      worksheetAfter ??= getWorksheetSnapshot();
      showStepLocation(
        location,
        () => apply(before, resultsBefore, worksheetBefore),
        targetExists
      );
    },
    redo: () => {
      showStepLocation(
        location,
        () => apply(after, resultsAfter, worksheetAfter),
        targetExists
      );
    },
  };
  return self;
}
